<template>
    <PopupTemplate
        v-if="shouldShow"
        v-model="isOpen"
        :img-raw="imgSrc"
        img-alt="Trial ended"
        img-class="trial-anim"
        :title="t('vue.extend_your_trial')"
        :body-text="t('vue.extend_your_trial_action_step')"
        @close="dismissed = true"
    >
        <template #actions>
            <AtomicButton
                color="yellow"
                size="lg"
                class="border-transparent bg-[#088BEA] text-white hover:text-gray-100 focus:ring-[#088BEA] disabled:bg-gray-300 disabled:text-white"
                @click="addDomainAction"
            >
                {{ t("vue.add_your_first_domain") }}
            </AtomicButton>
        </template>
    </PopupTemplate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import imgSrc from "@/assets/images/special-offer-celebration.svg?raw";
import PopupTemplate from "@/components/Atomic/Dialogs/PopupTemplate.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import { useTranslations } from "@/composables/useTranslations";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import type ICurrentTeam from "@/types/Members/ICurrentTeam.ts";

const { t } = useTranslations();
const page = usePage();

const allowedRoutesToSkip = new Set(["domainGuide.index", "domains.index"]);
const currentRouteName = route().current();

const shouldShow = computed(() => {
    const isTrialing = page.props.isTrialing;
    const hadActiveDomain = page.props.hadActiveDomain;

    const createdAtStr = page.props.team?.created_at;
    const trialEndsAtStr = page.props.team?.trial_ends_at;

    if (!createdAtStr || !trialEndsAtStr) return false;

    const createdAt = new Date(createdAtStr);
    const trialEndsAt = new Date(trialEndsAtStr);

    if (
        Number.isNaN(createdAt.getTime()) ||
        Number.isNaN(trialEndsAt.getTime())
    )
        return false;

    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const diffDays = (trialEndsAt.getTime() - createdAt.getTime()) / MS_PER_DAY;

    const within14Days = diffDays <= 14;

    return isTrialing && !hadActiveDomain && within14Days;
});

const isOpen = ref(false);
const dismissed = ref(false);

watch(
    shouldShow,
    (show) => {
        if (!show) {
            dismissed.value = false;
            isOpen.value = false;
            return;
        }
        if (!dismissed.value) isOpen.value = true;
    },
    { immediate: true },
);

watch(isOpen, (openNow) => {
    if (!openNow) dismissed.value = true;
});

function addDomainAction() {
    const isAllowed =
        currentRouteName != null && allowedRoutesToSkip.has(currentRouteName);

    if (!isAllowed) {
        router.visit(route("domains.index"));
    }

    isOpen.value = false;
    dismissed.value = true;
}
</script>
